import { Clock, Gamepad2, Map as MapIcon } from "lucide-react";

type MapHeroProps = {
  mapImageUrl: string | null;
  mapName: string | null;
  gameName: string | null;
  gameIconUrl: string | null;
  duration: string;
};

export function MapHero({ mapImageUrl, mapName, gameName, gameIconUrl, duration }: MapHeroProps) {
  return (
    <div className="relative rounded-xl overflow-hidden border border-surface-700/40">
      <div className="absolute inset-0 noise-overlay pointer-events-none z-[1]" />

      {mapImageUrl ? (
        <div className="relative h-44 sm:h-52">
          <img
            src={mapImageUrl}
            alt={mapName ?? "Map"}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-surface-900 via-surface-900/50 to-surface-900/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-surface-900/30 via-transparent to-surface-900/30" />

          <div className="relative z-10 flex items-center justify-between p-4">
            <div className="flex items-center gap-2 bg-surface-900/70 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-red-500/20">
              <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[11px] font-display font-bold uppercase tracking-wider text-red-400">
                Live
              </span>
            </div>
            <div className="flex items-center gap-1.5 bg-surface-900/70 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-surface-700/30">
              <Clock className="h-3.5 w-3.5 text-surface-400" />
              <span className="font-display font-bold text-sm text-white tabular-nums tracking-wide">
                {duration}
              </span>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
            <div className="flex items-center gap-2.5">
              <MapIcon className="h-5 w-5 text-brand-400" />
              <span className="font-display text-xl sm:text-2xl font-bold text-white drop-shadow-lg">
                {mapName}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative h-28 bg-gradient-to-br from-surface-800/80 via-surface-850 to-surface-900">
          <div className="relative z-10 flex items-center justify-between h-full px-5">
            <div className="flex items-center gap-3">
              {gameName && (
                <>
                  <div className="h-10 w-10 rounded-lg bg-surface-700/60 flex items-center justify-center overflow-hidden">
                    {gameIconUrl ? (
                      <img src={gameIconUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Gamepad2 className="h-5 w-5 text-brand-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-display text-lg font-bold text-white">{gameName}</p>
                    {mapName && <p className="text-xs text-surface-400">{mapName}</p>}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-surface-800/60 rounded-lg px-3 py-1.5">
                <Clock className="h-3.5 w-3.5 text-surface-400" />
                <span className="font-display font-bold text-sm text-white tabular-nums">
                  {duration}
                </span>
              </div>
              <div className="flex items-center gap-2 bg-red-500/15 rounded-lg px-3 py-1.5">
                <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[11px] font-display font-bold uppercase tracking-wider text-red-400">
                  Live
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
